import { useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";

// Route prefetch configuration based on navigation patterns
const PREFETCH_MAP: Record<string, string[]> = {
  "/dashboard": ["/dashboard/pages", "/dashboard/feedback"],
  "/dashboard/pages": ["/dashboard/feedback"],
  "/dashboard/feedback": ["/dashboard/analytics"],
  "/dashboard/analytics": ["/dashboard/reports"],
};

// Lazy imports for prefetching
const routeModules: Record<string, () => Promise<unknown>> = {
  "/dashboard": () => import("@/pages/Dashboard"),
  "/dashboard/pages": () => import("@/pages/FeedbackPages"),
  "/dashboard/feedback": () => import("@/pages/FeedbackView"),
  "/dashboard/analytics": () => import("@/pages/Analytics"),
  "/dashboard/reports": () => import("@/pages/Reports"),
  "/dashboard/settings": () => import("@/pages/Settings"),
  "/dashboard/campaigns": () => import("@/pages/Campaigns"),
  "/dashboard/hr-advisor": () => import("@/pages/AIHRAdvisor"),
  "/dashboard/ai-hub": () => import("@/pages/AIInsightsHub"),
};

// Track which routes have been prefetched to avoid duplicate attempts
const prefetchedRoutes = new Set<string>();

export function useRoutePrefetch() {
  const location = useLocation();
  const prefetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const prefetchRoute = useCallback((route: string) => {
    // Skip if already prefetched
    if (prefetchedRoutes.has(route)) return;

    const moduleLoader = routeModules[route];
    if (!moduleLoader) return;

    // Mark as prefetched before attempting
    prefetchedRoutes.add(route);

    // Use requestIdleCallback for non-blocking prefetch with error handling
    const doLoad = () => {
      moduleLoader().catch(() => {
        // Silently fail - the route will load normally when navigated to
        // Remove from prefetched so it can be retried later
        prefetchedRoutes.delete(route);
      });
    };

    if (typeof requestIdleCallback !== "undefined") {
      requestIdleCallback(doLoad, { timeout: 3000 });
    } else {
      setTimeout(doLoad, 200);
    }
  }, []);

  useEffect(() => {
    // Clear any pending prefetch when route changes
    if (prefetchTimeoutRef.current) {
      clearTimeout(prefetchTimeoutRef.current);
    }

    const routesToPrefetch = PREFETCH_MAP[location.pathname];
    if (routesToPrefetch && routesToPrefetch.length > 0) {
      // Delay prefetch to prioritize current page render
      prefetchTimeoutRef.current = setTimeout(() => {
        // Only prefetch the first route to reduce network contention
        prefetchRoute(routesToPrefetch[0]);
      }, 2000);

      return () => {
        if (prefetchTimeoutRef.current) {
          clearTimeout(prefetchTimeoutRef.current);
        }
      };
    }
  }, [location.pathname, prefetchRoute]);

  return { prefetchRoute };
}

// Component to add prefetch on hover
export function PrefetchLink({
  to,
  children,
  ...props
}: React.AnchorHTMLAttributes<HTMLAnchorElement> & { to: string }) {
  const { prefetchRoute } = useRoutePrefetch();

  const handleMouseEnter = useCallback(() => {
    prefetchRoute(to);
  }, [to, prefetchRoute]);

  return (
    <a onMouseEnter={handleMouseEnter} {...props}>
      {children}
    </a>
  );
}
